/**
 * use-mobile-segments.ts — Phân đoạn Hộp thư di động của người dùng, ĐỒNG BỘ giữa các thiết bị.
 *
 *   GET/PUT /me/ui-prefs/mobile-segments   { segments, updatedAt }   (backend: modules/me/ui-prefs*.ts, AppSetting `ui.mobile-segments.<userId>`)
 *
 * - localStorage `ecdp_mobile_segments:<userId>` là bộ nhớ đệm: hiện NGAY khi mở, không chớp chờ mạng.
 * - Mở trang / quay lại tab: lấy bản máy chủ về và hợp nhất (`mergePrefs`): bản `updatedAt` mới hơn thắng; máy chủ chưa có mà máy này có → đẩy lên.
 * - Sửa: cập nhật cục bộ ngay, PUT nền sau ~500ms (gộp nhiều lần sửa; rời trang thì gửi luôn). Lỗi mạng → giữ bản cục bộ, KHÔNG báo lỗi;
 *   lần hợp nhất sau thấy bản máy này mới hơn nên tự đẩy lại.
 * - Xem dưới quyền người khác: chỉ đọc, không ghi lên máy chủ (backend cũng chặn).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import {
  DEFAULT_SEGMENTS, loadPref, mergePrefs, normalizeSegments, parsePref, savePref,
  type SegmentId, type SegmentsPref,
} from '@/pages/conversations/mobile-segments'

const URL = '/me/ui-prefs/mobile-segments'
export const PUSH_DEBOUNCE_MS = 500

export function useMobileSegments(userId: string | undefined, opts: { tagNames?: string[]; readOnly?: boolean } = {}) {
  const { tagNames, readOnly } = opts
  const qc = useQueryClient()
  const [pref, setPref] = useState<SegmentsPref | null>(() => loadPref(userId))
  const prefRef = useRef(pref)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dirty = useRef<SegmentsPref | null>(null)
  const queryKey = useMemo(() => ['ui-prefs', 'mobile-segments', userId] as const, [userId])

  const apply = useCallback((next: SegmentsPref | null) => { prefRef.current = next; setPref(next) }, [])

  // Đổi người dùng (đăng nhập lại, xem dưới quyền) → nạp bộ nhớ đệm của người đó.
  useEffect(() => { apply(loadPref(userId)) }, [userId, apply])

  const push = useCallback(async (p: SegmentsPref) => {
    if (readOnly || !userId) return
    try {
      const { data } = await api.put<{ pref: SegmentsPref; applied: boolean }>(URL, p)
      qc.setQueryData(queryKey, { pref: data.pref })
      // Máy chủ đang giữ bản mới hơn (thiết bị khác vừa sửa) → nhận bản đó.
      if (!data.applied) {
        const server = parsePref(data.pref)
        if (server) { savePref(userId, server); apply(server) }
      }
    } catch { /* mạng lỗi / 403 → giữ bản cục bộ, lần hợp nhất sau tự thử lại */ }
  }, [readOnly, userId, qc, queryKey, apply])

  const flush = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null }
    const p = dirty.current
    dirty.current = null
    if (p) void push(p)
  }, [push])

  const schedule = useCallback((p: SegmentsPref) => {
    dirty.current = p
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(flush, PUSH_DEBOUNCE_MS)
  }, [flush])

  // Rời màn Hộp thư (vào hội thoại…) khi còn bản chờ gửi → gửi luôn.
  useEffect(() => () => flush(), [flush])

  const remote = useQuery<{ pref: SegmentsPref | null }>({
    queryKey,
    enabled: !!userId,
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    queryFn: async () => {
      const { data } = await api.get<{ pref: unknown }>(URL)
      return { pref: parsePref(data.pref) }
    },
  })

  // Có bản máy chủ (kể cả "chưa có") → hợp nhất. Đọc bản cục bộ MỚI NHẤT qua ref (người dùng có thể vừa sửa trong lúc chờ mạng).
  useEffect(() => {
    if (!remote.data || !userId) return
    const { pref: merged, action } = mergePrefs(prefRef.current, remote.data.pref)
    if (action === 'adopt-remote' && merged) { savePref(userId, merged); apply(merged) }
    else if (action === 'push-local' && merged) schedule(merged)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remote.dataUpdatedAt, userId])

  /** Người dùng sửa: áp ngay + lưu đệm + đẩy nền. */
  const setSegments = useCallback((segments: SegmentId[]) => {
    const next: SegmentsPref = { segments, updatedAt: new Date().toISOString() }
    savePref(userId, next)
    apply(next)
    schedule(next)
  }, [userId, apply, schedule])

  // Phân đoạn nhãn đã xoá thì bỏ khi đã biết danh sách nhãn; chưa có bản nào → mặc định.
  const segments = useMemo(() => normalizeSegments(pref?.segments ?? DEFAULT_SEGMENTS, tagNames), [pref, tagNames])
  return { segments, setSegments }
}
